import React from 'react';
import { motion } from 'framer-motion';
import { UserIcon, BotIcon } from 'lucide-react';
import { StatusIndicator, StatusType } from './StatusIndicator';
import { FileText, ExternalLink } from 'lucide-react';
import { copy } from '../../copy';
export type MessageRole = 'user' | 'assistant';
export interface MessageSource {
  id: string;
  name: string;
  type: string;
}
export interface MessageStep {
  type: StatusType;
  label: string;
  content?: string;
}
export interface MessageProps {
  role: MessageRole;
  content: string;
  steps?: MessageStep[];
  sources?: MessageSource[];
}
export function Message({
  role,
  content,
  steps,
  sources
}: MessageProps) {
  const isAssistant = role === 'assistant';
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.3,
    ease: 'easeOut'
  }} className={`flex w-full gap-4 py-8 ${isAssistant ? 'bg-slate-50/50 dark:bg-[#222]/50' : 'bg-transparent'}`} data-id="element-43">
      <div className="mx-auto flex w-full max-w-3xl gap-4 px-4" data-id="element-44">
        <div className={`flex h-8 w-8 shrink-0 select-none items-center justify-center rounded-md border shadow-sm ${isAssistant ? 'bg-white dark:bg-[#1a1a1a] text-slate-600 dark:text-[#a3a3a3] border-slate-200 dark:border-[#333]' : 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 border-slate-900 dark:border-white'}`} data-id="element-45">
          {isAssistant ? <img src="https://cdn.magicpatterns.com/uploads/wBrpemDYzZmjFSuxiYRjhJ/Imperian.png" alt="Imperian Logo" className="h-5 w-auto" data-id="element-46" /> : <UserIcon size={18} data-id="element-47" />}
        </div>

        <div className="flex-1 space-y-2 overflow-hidden px-1" data-id="element-48">
          {steps && steps.length > 0 && <div className="flex flex-col gap-1 mb-4" data-id="element-49">
              {steps.map((step, i) => <StatusIndicator key={i} type={step.type} label={step.label} isExpanded={i === steps.length - 1 && step.type !== 'complete'} data-id="element-50" />)}
            </div>}

          {content && <div className="prose prose-slate dark:prose-invert max-w-none break-words leading-7 text-slate-700 dark:text-[#e5e5e5]" data-id="element-51">
              {content.split('\n').map((line, i) => <p key={i} className={line.trim() === '' ? 'h-4' : ''} data-id="element-52">
                  {line}
                </p>)}
            </div>}

          {sources && sources.length > 0 && <div className="mt-6 pt-6 border-t border-slate-100 dark:border-[#262626]" data-id="element-53">
              <div className="text-[10px] font-bold text-slate-400 dark:text-[#555] uppercase tracking-wider mb-3" data-id="element-54">
                {copy.thread.citedSourcesLabel}
              </div>
              <div className="flex flex-wrap gap-2" data-id="element-55">
                {sources.map(source => <button key={source.id} className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-[#333] bg-white dark:bg-[#1a1a1a] hover:border-slate-300 dark:hover:border-[#444] transition-all group" data-id="element-56">
                    <FileText size={14} className="text-slate-400 dark:text-[#555] group-hover:text-blue-500" data-id="element-57" />
                    <span className="text-xs font-medium text-slate-600 dark:text-[#a3a3a3] group-hover:text-slate-900 dark:group-hover:text-white" data-id="element-58">
                      {source.name}
                    </span>
                    <ExternalLink size={12} className="text-slate-300 dark:text-[#333] opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-59" />
                  </button>)}
              </div>
            </div>}
        </div>
      </div>
    </motion.div>;
}